import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { toAssetUrl } from '../utils/assetBase';
import SearchSelect from './SearchSelect';

// Menyu çapı / PDF modalı — printerə və ya kateqoriyaya görə süzülmüş menyu siyahısı.
// Sütunları tick ilə seç: şəkil, kateqoriya, satış növü, maya (alış), texkarta, satış qiyməti.
// Çap A4 printerə (desktop) və ya brauzer çap dialoquna (web) gedir; PDF ixracı desktopda
// FiscalPOS qovluğuna avtomatik yazılır (C# 'export-pdf' körpüsü), webdə çap→PDF dialoqu açılır.

const SALE_TYPE_LABEL = { piece: 'əd', weight: 'kq', liter: 'lt' };
const esc = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
const money = (v) => { const n = Number(v || 0); return Number.isInteger(n) ? String(n) : n.toFixed(2); };
const tn = (n) => { n = Number(n) || 0; return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000); };

const fmtQty = (q, unit) => {
    q = Number(q) || 0;
    if (unit === 'eded') return `${tn(q)} əd`;
    if (unit === 'kg') return q < 1 ? `${Math.round(q * 1000)} q` : `${tn(q)} kq`;
    if (unit === 'litr' || unit === 'lt') return q < 1 ? `${Math.round(q * 1000)} ml` : `${tn(q)} lt`;
    if (unit === 'gram') return `${tn(q)} q`;
    if (unit === 'ml') return `${tn(q)} ml`;
    return `${tn(q)} ${unit}`;
};

const hasWebView = () => typeof window !== 'undefined' && window.chrome?.webview?.postMessage;

export default function MenuPrintModal({ open, onClose, printers = [] }) {
    const [loading, setLoading]   = useState(false);
    const [data, setData]         = useState(null);          // {lines, printers, categories}
    const [recipeMap, setRecipeMap] = useState({});          // item_id → [{name,qty,unit,cost}]

    const [mode, setMode]         = useState('printer');     // 'printer' | 'category'
    const [printer, setPrinter]   = useState('');
    const [category, setCategory] = useState('');

    const [cols, setCols] = useState({
        image: false, category: true, saleType: false, cost: false, recipe: false, recipeCost: true, price: true,
    });

    const [previewHtml, setPreviewHtml] = useState('');
    const [exporting, setExporting]     = useState(false);
    const [progress, setProgress]       = useState(0);

    // ── Məlumatı yüklə ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const [cat, rRes, iRes] = await Promise.all([
                    api.get('/menu/print-catalog'),
                    api.get('/recipes/all').catch(() => ({ data: { data: [] } })),
                    api.get('/ingredients/all').catch(() => ({ data: { data: [] } })),
                ]);
                if (!alive) return;
                setData(cat.data);

                const ingUnit = {}, ingCost = {};
                (iRes.data.data || []).forEach(i => {
                    ingUnit[i.id] = i.unit;
                    ingCost[i.id] = Number(i.last_purchase_price ?? i.cost_per_unit) || 0;
                });
                const rm = {};
                (rRes.data.data || []).forEach(r => {
                    if (!rm[r.menu_item_id]) rm[r.menu_item_id] = [];
                    rm[r.menu_item_id].push({
                        name: r.ingredient_name,
                        qty: Number(r.quantity) || 0,
                        unit: ingUnit[r.ingredient_id] || r.unit || 'kg',
                        cost: ingCost[r.ingredient_id] || 0,
                    });
                });
                setRecipeMap(rm);
            } catch {
                if (alive) toast.error('Menyu məlumatı yüklənmədi.');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [open]);

    // Modal bağlananda önizləməni təmizlə
    useEffect(() => { if (!open) { setPreviewHtml(''); setExporting(false); setProgress(0); } }, [open]);

    // ── Süzülmüş sətirlər ───────────────────────────────────────────────────
    const filteredLines = useCallback(() => {
        const lines = data?.lines || [];
        return lines.filter(l => {
            if (mode === 'printer' && printer) return l.printer === printer;
            if (mode === 'category' && category) return String(l.category_id) === String(category);
            return true;
        });
    }, [data, mode, printer, category]);

    const currentTitle = useCallback(() => {
        if (mode === 'printer' && printer) {
            const p = (data?.printers || []).find(x => x.name === printer);
            return p?.label || printer;
        }
        if (mode === 'category' && category) {
            const c = (data?.categories || []).find(x => String(x.id) === String(category));
            return c?.name || 'Menyu';
        }
        return 'Menyu';
    }, [data, mode, printer, category]);

    // ── HTML qur ────────────────────────────────────────────────────────────
    const buildHtml = useCallback(() => {
        const rows = filteredLines();
        if (!rows.length) return null;
        const title = currentTitle();
        const dateStr = new Date().toLocaleDateString('az');
        const count = rows.length;

        const useTable = cols.image || cols.saleType || cols.cost || cols.recipe;

        const recipeStr = (itemId) => {
            const recipe = recipeMap[itemId] || [];
            if (!recipe.length) return '<em style="color:#999">—</em>';
            return recipe.map(r => {
                const line = r.qty * r.cost;
                // Texkartada inqredient qiyməti (alış) — yalnız tick olduqda
                const cp = (cols.recipeCost && r.cost > 0) ? ` = <b>${line.toFixed(2)}₼</b>` : '';
                return `${esc(r.name)} ${fmtQty(r.qty, r.unit)}${cp}`;
            }).join('<br>');
        };

        let bodyHtml, css;

        if (!useTable) {
            // Sadə axan sütun düzülüşü (ad + qiymət) — 1 A4-ə maksimum yerləşdirmə
            const flowCols = count > 90 ? 3 : (count > 34 ? 2 : 1);
            const dense = count > 130;
            const fBase = dense ? 8.2 : (count > 90 ? 9 : 9.8);
            const fCat = dense ? 9 : (count > 90 ? 10 : 11);
            const pad = dense ? '1.6pt 5pt' : '2.4pt 6pt';
            let body = '', lastCat = null, opened = false;
            rows.forEach(it => {
                if (cols.category && it.category !== lastCat) {
                    if (opened) body += '</div>';
                    body += `<div class="cat"><div class="ch">${esc(it.category || '—')}</div>`;
                    opened = true; lastCat = it.category;
                }
                const label = it.variant ? `${esc(it.name)} <span class="v">— ${esc(it.variant)}</span>` : esc(it.name);
                const price = cols.price ? `<span class="pr">${money(it.price)} ₼</span>` : '';
                body += `<div class="row"><span class="nm">${label}</span>${price}</div>`;
            });
            if (opened) body += '</div>';
            if (!cols.category) body = `<div class="cat">${body}</div>`;
            bodyHtml = `<div class="cols">${body}</div>`;
            css = `.cols{column-count:${flowCols};column-gap:10pt}
                .cat{break-inside:avoid-column;-webkit-column-break-inside:avoid;margin-bottom:5pt}
                .ch{font-size:${fCat}pt;font-weight:800;border-bottom:1pt solid #000;padding:0 0 1.5pt;margin-bottom:1.5pt}
                .row{display:flex;justify-content:space-between;align-items:baseline;gap:6pt;padding:${pad};border-bottom:0.4pt solid #bbb}
                .nm{text-align:left}.nm .v{font-weight:400;font-style:italic}
                .pr{text-align:right;white-space:nowrap;font-weight:800}
                body{font-size:${fBase}pt}`;
        } else {
            // Tam cədvəl (seçilmiş sütunlarla)
            const fBase = cols.recipe ? (count > 60 ? 8.5 : 9.5) : (count > 120 ? 8 : count > 70 ? 9 : 10);
            const th = [];
            if (cols.image) th.push('<th style="width:44pt">Şəkil</th>');
            th.push('<th>Məhsul</th>');
            if (cols.category) th.push('<th style="width:14%">Kateqoriya</th>');
            if (cols.saleType) th.push('<th style="width:8%">Növ</th>');
            if (cols.cost) th.push('<th class="num" style="width:9%">Maya</th>');
            if (cols.recipe) th.push('<th>Texkarta</th>');
            if (cols.price) th.push('<th class="num" style="width:9%">Satış</th>');

            let body = '';
            rows.forEach(it => {
                const label = it.variant ? `${esc(it.name)} <span class="v">— ${esc(it.variant)}</span>` : esc(it.name);
                const tds = [];
                if (cols.image) {
                    const src = it.image_url ? toAssetUrl(it.image_url) : '';
                    tds.push(`<td class="img">${src ? `<img src="${esc(src)}" alt="">` : ''}</td>`);
                }
                tds.push(`<td class="name">${label}</td>`);
                if (cols.category) tds.push(`<td>${esc(it.category || '—')}</td>`);
                if (cols.saleType) tds.push(`<td>${SALE_TYPE_LABEL[it.sale_type] || 'əd'}</td>`);
                if (cols.cost) tds.push(`<td class="num">${Number(it.cost) > 0 ? money(it.cost) + ' ₼' : '—'}</td>`);
                if (cols.recipe) tds.push(`<td class="recipe">${recipeStr(it.item_id)}</td>`);
                if (cols.price) tds.push(`<td class="num">${Number(it.price) > 0 ? money(it.price) + ' ₼' : '—'}</td>`);
                body += `<tr>${tds.join('')}</tr>`;
            });
            bodyHtml = `<table><thead><tr>${th.join('')}</tr></thead><tbody>${body}</tbody></table>`;
            css = `table{width:100%;border-collapse:collapse;background:#fff}
                th,td{border:0.5pt solid #000;padding:3pt 5pt;vertical-align:top;text-align:left;background:#fff;color:#000}
                th{font-size:${fBase - 0.5}pt;font-weight:800;border-bottom:1.2pt solid #000}
                td.name{font-weight:bold}td.name .v{font-weight:400;font-style:italic}
                td.recipe{font-size:${fBase - 1}pt}
                td.num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;font-weight:bold}
                td.img{text-align:center;padding:2pt}td.img img{max-width:38pt;max-height:38pt;object-fit:cover}
                body{font-size:${fBase}pt}`;
        }

        return `<!doctype html><html><head><meta charset="utf-8"><title>Menyu — ${esc(title)}</title><style>
            @page{size:A4 portrait;margin:8mm}*{box-sizing:border-box}html,body{margin:0;padding:0}
            body{font-family:'Segoe UI',Tahoma,'DejaVu Sans',Arial,sans-serif;line-height:1.2;color:#000;background:#fff;padding:0}
            h1{font-size:16pt;margin:0;text-align:center;letter-spacing:0.3pt}
            .meta{text-align:center;font-size:8pt;color:#000;margin:1pt 0 6pt}
            .foot{margin-top:10pt;text-align:center;font-size:7.5pt;color:#000;break-inside:avoid}
            ${css}
        </style></head><body>
            <h1>${esc(title)}</h1>
            <div class="meta">${dateStr} — ${count} məhsul</div>
            ${bodyHtml}
            <div class="foot">powered by www.fiscalbusiness.net</div>
        </body></html>`;
    }, [filteredLines, currentTitle, recipeMap, cols]);

    // Filter/sütun/seçim dəyişəndə önizləməni auto yenilə (məlumat yükləndikdən sonra)
    useEffect(() => {
        if (!open || loading || !data) return;
        setPreviewHtml(buildHtml() || '');
    }, [open, loading, data, buildHtml]);

    // ── Əməliyyatlar ────────────────────────────────────────────────────────
    const doPreview = () => {
        const html = buildHtml();
        if (!html) { toast.error('Bu seçimə uyğun məhsul yoxdur.'); return; }
        setPreviewHtml(html);
    };

    const webPrintWindow = (html) => {
        const win = window.open('', '_blank');
        if (!win) { toast.error('Popup bloklandı — brauzerdə icazə verin.'); return; }
        win.document.write(html); win.document.close(); win.focus();
        setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 500);
    };

    const doPrint = () => {
        const html = buildHtml();
        if (!html) { toast.error('Bu seçimə uyğun məhsul yoxdur.'); return; }
        if (hasWebView()) {
            const a4 = printers.find(p => p.printer_type === 'a4' && p.active);
            if (!a4) { toast.error('A4 printer təyin olunmayıb. Admin → Printerlər səhifəsində A4 printer əlavə edin.'); return; }
            window.chrome.webview.postMessage({ type: 'print-a4', printer: a4.name, html });
            toast.success(`${currentTitle()} → ${a4.name}`);
        } else {
            webPrintWindow(html);
        }
    };

    // PDF: desktopda C#-ə 'export-pdf' göndər (FiscalPOS qovluğuna yazır, faiz bildirir);
    // webdə çap dialoqu (istifadəçi "PDF olaraq saxla" seçir).
    const exportListenerRef = useRef(null);
    const doExportPdf = () => {
        const html = buildHtml();
        if (!html) { toast.error('Bu seçimə uyğun məhsul yoxdur.'); return; }
        const safe = (currentTitle() || 'menyu').replace(/[^\p{L}\p{N}]+/gu, '_').slice(0, 40);
        const filename = `Menyu_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;

        if (hasWebView()) {
            setExporting(true); setProgress(0);
            // Faiz və nəticə bildirişlərini dinlə
            if (exportListenerRef.current) {
                try { window.chrome.webview.removeEventListener('message', exportListenerRef.current); } catch { /* ignore */ }
            }
            const handler = (event) => {
                const p = event?.data;
                if (!p || typeof p !== 'object') return;
                if (p.type === 'export-pdf-progress') {
                    setProgress(Math.max(0, Math.min(100, Number(p.percent) || 0)));
                } else if (p.type === 'export-pdf-result') {
                    try { window.chrome.webview.removeEventListener('message', handler); } catch { /* ignore */ }
                    exportListenerRef.current = null;
                    setExporting(false);
                    if (p.ok) { setProgress(100); toast.success(`PDF yadda saxlanıldı: ${p.path || filename}`); }
                    else toast.error(p.error || 'PDF ixracı baş tutmadı.');
                }
            };
            exportListenerRef.current = handler;
            window.chrome.webview.addEventListener('message', handler);
            window.chrome.webview.postMessage({ type: 'export-pdf', filename, html });
            // C# köhnə versiyada bu tipi tanımasa — 25 s sonra düşür
            setTimeout(() => {
                if (exportListenerRef.current === handler) {
                    try { window.chrome.webview.removeEventListener('message', handler); } catch { /* ignore */ }
                    exportListenerRef.current = null;
                    setExporting(false);
                    toast.error('PDF cavabı gəlmədi — desktop proqramı yeniləyin (export-pdf dəstəyi).');
                }
            }, 25000);
        } else {
            toast('Çap dialoqunda “PDF olaraq saxla” seçin.', { icon: '📄' });
            webPrintWindow(html);
        }
    };

    useEffect(() => () => {
        if (exportListenerRef.current && hasWebView()) {
            try { window.chrome.webview.removeEventListener('message', exportListenerRef.current); } catch { /* ignore */ }
        }
    }, []);

    const toggleCol = useCallback((k) => setCols(c => ({ ...c, [k]: !c[k] })), []);

    if (!open) return null;

    const printerOpts = (data?.printers || []).map(p => ({ value: p.name, label: p.label || p.name }));
    const catOpts = (data?.categories || []).map(c => ({ value: c.id, label: c.name }));

    const COL_DEFS = [
        { k: 'image', label: '📷 Şəkil' },
        { k: 'category', label: '🏷️ Kateqoriya' },
        { k: 'saleType', label: '⚖️ Satış növü (əd/kq/lt)' },
        { k: 'cost', label: '💰 Maya (alış)' },
        { k: 'recipe', label: '📋 Texkarta' },
        { k: 'recipeCost', label: '💵 Texkartada qiymət', dep: 'recipe' },
        { k: 'price', label: '🏷️ Satış qiyməti' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
                {/* Başlıq */}
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                    <h2 className="text-base font-bold text-gray-800">🖨️ Menyu çapı / PDF</h2>
                    <button onClick={onClose} className="rounded-lg px-2 py-1 text-xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700">×</button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {loading ? (
                        <div className="py-16 text-center text-sm text-gray-400">Yüklənir…</div>
                    ) : (
                        <>
                            {/* Süzgəc rejimi */}
                            <div className="mb-3 flex gap-2">
                                <button onClick={() => setMode('printer')}
                                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold transition-all ${mode === 'printer' ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500'}`}>
                                    Printerə görə
                                </button>
                                <button onClick={() => setMode('category')}
                                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold transition-all ${mode === 'category' ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500'}`}>
                                    Kateqoriyaya görə
                                </button>
                            </div>

                            {/* Combobox (axtarışlı) */}
                            <div className="mb-4">
                                {mode === 'printer' ? (
                                    <SearchSelect value={printer} onChange={setPrinter} options={printerOpts}
                                        placeholder="🔍 Printer seç (boş = hamısı)" />
                                ) : (
                                    <SearchSelect value={category} onChange={setCategory} options={catOpts}
                                        placeholder="🔍 Kateqoriya seç (boş = hamısı)" />
                                )}
                                <p className="mt-1 text-[11px] text-gray-400">Boş buraxsanız — bütün menyu çap olunur.</p>
                            </div>

                            {/* Sütun seçimləri */}
                            <div className="mb-2 text-xs font-semibold text-gray-600">Vərəqdə göstər:</div>
                            <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {COL_DEFS.map(c => {
                                    const depOff = c.dep && !cols[c.dep];
                                    return (
                                        <label key={c.k}
                                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${depOff ? 'cursor-not-allowed border-gray-100 text-gray-300' : `cursor-pointer ${cols[c.k] ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500'}`} ${c.dep ? 'ml-3' : ''}`}
                                            title={depOff ? 'Əvvəlcə “Texkarta” tikini açın' : undefined}>
                                            <input type="checkbox" checked={cols[c.k]} disabled={depOff} onChange={() => toggleCol(c.k)}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                                            {c.label}
                                        </label>
                                    );
                                })}
                            </div>

                            {/* İxrac faiz zolağı (desktop PDF) */}
                            {exporting && (
                                <div className="mb-4">
                                    <div className="mb-1 flex justify-between text-[11px] font-medium text-gray-500">
                                        <span>PDF hazırlanır…</span><span>{progress}%</span>
                                    </div>
                                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                        <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${progress}%` }} />
                                    </div>
                                </div>
                            )}

                            {/* Önizləmə */}
                            {previewHtml && (
                                <div className="mb-2">
                                    <div className="mb-1 text-xs font-semibold text-gray-600">Önizləmə:</div>
                                    <iframe title="Önizləmə" srcDoc={previewHtml}
                                        className="h-[420px] w-full rounded-lg border border-gray-200 bg-white" />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Düymələr */}
                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
                    <button onClick={doPreview} disabled={loading}
                        className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-50">
                        👁️ Önizləmə
                    </button>
                    <button onClick={doExportPdf} disabled={loading || exporting}
                        className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-50">
                        📄 PDF ixrac
                    </button>
                    <button onClick={doPrint} disabled={loading}
                        className="rounded-lg px-4 py-2 text-xs font-bold text-white transition-all disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                        🖨️ Çap et
                    </button>
                </div>
            </div>
        </div>
    );
}
